import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, PauseCircle, PlayCircle, Layers } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listStoreBundleDeals,
  createStoreBundleDeal,
  updateStoreBundleDeal,
  deleteStoreBundleDeal,
} from "@/lib/api/admin";
import type { BundleDeal } from "@/lib/api/types";
import { ApiError } from "@/lib/api/fetch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { formatCents } from "@/lib/utils/number";
import { BundleDealFormDialog } from "../dialogs/BundleDealFormDialog";

function bundleDealErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === "product_bundle_price_not_discounted") {
    return "Combo price must be less than the combined regular price of the items.";
  }
  if (err instanceof ApiError && err.code === "product_bundle_min_quantity") {
    return "The combo must include at least 2 items total.";
  }
  if (err instanceof ApiError && err.code === "product_bundle_item_not_found") {
    return "One or more selected items could not be found.";
  }
  return (err as Error).message;
}

type BundleDealsTabProps = {
  storeId: string;
};

export function BundleDealsTab({ storeId }: BundleDealsTabProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingItem, setEditingItem] = useState<BundleDeal | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<BundleDeal | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    data: bundleDeals,
    isLoading,
    refetch: refetchBundleDeals,
  } = useQuery({
    queryKey: ["bundle-deals", storeId],
    queryFn: () => listStoreBundleDeals(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["bundle-deals", storeId] });
  }

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createStoreBundleDeal>[2]) =>
      createStoreBundleDeal(user!, storeId, data),
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      toast.success("Combo created.");
    },
    onError: (err) => setApiError(bundleDealErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateStoreBundleDeal>[3] }) =>
      updateStoreBundleDeal(user!, storeId, id, data),
    onSuccess: () => {
      invalidate();
      setFormOpen(false);
      setEditingItem(undefined);
      toast.success("Combo updated.");
    },
    onError: (err) => setApiError(bundleDealErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteStoreBundleDeal(user!, storeId, id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast.success("Combo deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (bd: BundleDeal) =>
      updateStoreBundleDeal(user!, storeId, bd.id, {
        name: bd.name,
        bundle_price_cents: bd.bundle_price_cents,
        max_claims_per_customer_per_day: bd.max_claims_per_customer_per_day,
        schedule: bd.schedule,
        status: bd.status === "active" ? "inactive" : "active",
        items: bd.items.map((i) => ({ menu_item_id: i.menu_item.id, quantity: i.quantity })),
      }),
    onSuccess: (_, bd) => {
      invalidate();
      toast.success(bd.status === "active" ? "Combo paused." : "Combo activated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const formPending = createMutation.isPending || updateMutation.isPending;

  function handleOpenAdd() {
    setEditingItem(undefined);
    setApiError(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  async function handleOpenEdit(bd: BundleDeal) {
    // Refresh first — the dialog seeds its item rows once on mount, from whatever it's given.
    const refreshed = await refetchBundleDeals();
    const fresh = refreshed.data?.find((d) => d.id === bd.id) ?? bd;
    setEditingItem(fresh);
    setApiError(null);
    setFormKey((k) => k + 1);
    setFormOpen(true);
  }

  function handleSave(data: {
    name: string;
    bundle_price_cents: number;
    max_claims_per_customer_per_day: number | null;
    schedule: Record<string, { open: string; close: string }[]>;
    status: "active" | "inactive";
    items: { menu_item_id: string; quantity: number }[];
  }) {
    setApiError(null);
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  const items = bundleDeals ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <Button size="sm" onClick={handleOpenAdd}>
          <Plus className="size-4 mr-1" /> Add Combo
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No combos"
          description='Create a fixed-price combo like "Burger + Beer for $18" or "2 Burgers for $12".'
        />
      ) : (
        <div className="space-y-2">
          {items.map((bd) => {
            const itemCount = bd.items.length;
            const totalQuantity = bd.items.reduce((sum, i) => sum + i.quantity, 0);
            const savingsIsPositive = bd.savings_cents > 0;
            return (
              <Card
                key={bd.id}
                onClick={() => handleOpenEdit(bd)}
                className="cursor-pointer hover:border-primary/50"
              >
                <CardContent className="flex items-center gap-3 p-3">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{bd.name}</p>
                      <StatusBadge status={bd.status} />
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-foreground">
                        {formatCents(bd.bundle_price_cents)}
                      </span>
                      <span className="line-through">{formatCents(bd.regular_price_cents)}</span>
                      <span className={savingsIsPositive ? "text-green-700" : "text-destructive"}>
                        {savingsIsPositive ? "−" : "+"}
                        {Math.abs(bd.savings_percent).toFixed(0)}%
                      </span>
                      <span>
                        · {itemCount} item{itemCount === 1 ? "" : "s"}
                        {totalQuantity !== itemCount && ` (${totalQuantity} units)`}
                      </span>
                      {bd.max_claims_per_customer_per_day != null && (
                        <span>· max {bd.max_claims_per_customer_per_day}/day per customer</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      title={bd.status === "active" ? "Pause" : "Activate"}
                      disabled={toggleStatusMutation.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStatusMutation.mutate(bd);
                      }}
                    >
                      {bd.status === "active" ? (
                        <PauseCircle className="size-3.5" />
                      ) : (
                        <PlayCircle className="size-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(bd);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete combo"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />

      <BundleDealFormDialog
        key={formKey}
        open={formOpen}
        onOpenChange={setFormOpen}
        storeId={storeId}
        item={editingItem}
        onSave={handleSave}
        isPending={formPending}
        apiError={apiError}
      />
    </div>
  );
}
